import { useMemo, useState } from 'react';
import { Check, Copy, ExternalLink, TriangleAlert } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Skeleton } from '@/components/atoms/skeleton';
import { BrandLogo } from '@/components/organisms/settings-integrations/BrandLogo';
import { WisetackWhyFinancingBox } from '@/components/organisms/financing/WisetackWhyFinancingBox';
import {
  WISETACK_BRAND,
  WISETACK_CARD_SUBTITLE,
  WISETACK_CARD_TITLE,
  WISETACK_LEARN_MORE_URL,
  wisetackPortalUrl,
  wisetackSignupUrl,
} from '@/lib/wisetack/brand';
import {
  WISETACK_APPLICATION_STATUS_MAP,
  WISETACK_APPLICATION_STATUSES,
  type WisetackApplicationStatus,
} from '@notifi/shared-types/wisetack-application-status';
import { useSimulateWisetackStatus } from '@/hooks/settings/useSimulateWisetackStatus';
import { useHasAnyPermission } from '@/hooks/usePermissions';
import { PermissionHint } from '@/components/organisms/settings-integrations/IntegrationCard';
import type { WisetackEnvironment, WisetackSettings } from '@/types/settings';
import { WISETACK_DEFAULT_SETTINGS } from './wisetack-settings-form.constants';

const ENVIRONMENT_OPTIONS: Array<{ value: WisetackEnvironment; label: string }> = [
  { value: 'mock', label: 'Mock' },
  { value: 'sandbox', label: 'Sandbox' },
  { value: 'production', label: 'Production' },
];

const STATE_LABELS: Record<WisetackSettings['enrollmentState'], string> = {
  not_configured: 'Not enrolled',
  not_enrolled: 'Not enrolled',
  in_progress: 'Application in progress',
  submitted: 'Under review',
  approved: 'Approved',
  declined: 'Declined',
  // Wisetack treats an expired application as never-enrolled: the merchant's
  // only move is a fresh signup, which is exactly the not-enrolled surface.
  expired: 'Not enrolled',
};

interface WisetackSettingsFormProps {
  settings?: WisetackSettings;
  isLoading?: boolean;
}

export function WisetackSettingsForm({ settings, isLoading = false }: WisetackSettingsFormProps) {
  const [copied, setCopied] = useState(false);
  const displaySettings = useMemo(() => toDisplaySettings(settings), [settings]);
  const signupUrl = useMemo(() => wisetackSignupUrl(displaySettings), [displaySettings]);
  const enrollmentUrl =
    displaySettings.enrollmentState === 'in_progress' ? displaySettings.signupUrl : signupUrl;

  async function copyPrequalLink() {
    if (!displaySettings.prequalLink) return;
    await navigator.clipboard.writeText(displaySettings.prequalLink);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">Merchant enrollment</p>
          <p className="text-xs text-muted-foreground">
            Updated from the Wisetack merchant report
            {displaySettings.lastMerchantReportAt
              ? ` · ${displaySettings.lastMerchantReportAt.slice(0, 10)}`
              : ''}
          </p>
        </div>
        <Badge variant={displaySettings.enrollmentState === 'approved' ? 'default' : 'secondary'}>
          {STATE_LABELS[displaySettings.enrollmentState]}
        </Badge>
      </div>

      {acceptsDocuments(displaySettings) ? (
        <RequestedDocumentsNotice
          documents={displaySettings.requestedDocuments}
          environment={displaySettings.environment}
        />
      ) : null}

      {renderEnrollmentContent({
        settings: displaySettings,
        enrollmentUrl,
        copied,
        onCopy: copyPrequalLink,
      })}

      {displaySettings.statusSimulatorEnabled ? (
        <StatusSimulator settings={displaySettings} />
      ) : null}
    </div>
  );
}

function renderEnrollmentContent({
  settings,
  enrollmentUrl,
  copied,
  onCopy,
}: {
  settings: WisetackSettings;
  enrollmentUrl: string | null;
  copied: boolean;
  onCopy: () => Promise<void>;
}) {
  if (!settings.enabled) {
    return (
      <p className="text-sm text-muted-foreground">
        Wisetack is not available for this company. Contact your Notifi administrator.
      </p>
    );
  }

  // Approval is the whole story. `transactionsEnabled` is true by default for
  // an approved merchant (Wisetack feedback deck, slide 4), so branching on it
  // only ever produced a "you are approved but cannot transact" panel for a
  // report row that had not caught up yet.
  if (settings.enrollmentState === 'approved') {
    return (
      <div className="space-y-3">
        <div className="flex gap-2">
          <Input aria-label="Prequal link" value={settings.prequalLink ?? ''} readOnly />
          <Button type="button" variant="outline" disabled={!settings.prequalLink} onClick={onCopy}>
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <MerchantTerms
          transactionRange={settings.transactionRange}
          pricingPlan={settings.pricingPlan}
        />
        <WisetackPortalLinks environment={settings.environment} />
      </div>
    );
  }

  if (settings.enrollmentState === 'submitted') {
    // "No action required" is a lie while Wisetack is waiting on paperwork —
    // the documents notice above is the action.
    return (
      <p className="text-sm text-muted-foreground">
        {acceptsDocuments(settings)
          ? 'Wisetack is reviewing the application and is waiting on the documents above.'
          : 'Wisetack is reviewing the application. No action is required right now.'}
      </p>
    );
  }

  if (settings.enrollmentState === 'declined') {
    return (
      <p className="text-sm text-muted-foreground">
        Wisetack declined this application. A new application is unavailable for at least 90 days.
      </p>
    );
  }

  // An application already under way just needs a nudge to finish — no upsell.
  // Notifi passes `externalId` on every signup link, so Wisetack maps a resumed
  // application back to the same merchant; warning about duplicates described a
  // risk that does not exist (feedback deck, slide 4).
  if (settings.enrollmentState === 'in_progress') {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Pick up the application where you left off.</p>
        {enrollmentUrl ? (
          <Button asChild type="button">
            <a href={enrollmentUrl} target="_blank" rel="noreferrer">
              Continue application
              <ExternalLink className="size-4" />
            </a>
          </Button>
        ) : (
          <Button type="button" disabled>
            Continue application
            <ExternalLink className="size-4" />
          </Button>
        )}
      </div>
    );
  }

  // not_configured / not_enrolled / expired → Wisetack's branded signup card.
  return <WisetackSignupCard enrollmentUrl={enrollmentUrl} />;
}

/**
 * Wisetack asks for documents during underwriting, which starts when the
 * application is submitted — so `submitted` is the only state where the request
 * is live. Every other row can carry a stale `requested_documents` value the
 * report never cleared, and an upload CTA there points at an action that
 * changes nothing. `in_progress` in particular buckets the pre-submission steps
 * (PENDING, BUSINESS_ADDED, OWNERS_ADDED…, see mapApplicationStatus): the
 * merchant's real next step is finishing the form, and an upload prompt beside
 * "Continue application" is two contradictory instructions at once.
 */
function acceptsDocuments(settings: WisetackSettings): boolean {
  if (!settings.enabled) return false;
  if (settings.enrollmentState !== 'submitted') return false;
  return requestedDocumentList(settings.requestedDocuments).length > 0;
}

// Values Wisetack uses to mean "nothing outstanding". Without this an "N/A"
// cell pops the alert and flips the review copy to "waiting on documents".
const NO_DOCUMENTS_SENTINELS = new Set(['none', 'n/a', 'na', '-', '--', '—']);

/**
 * The CSV ships the list in one cell and does not commit to a separator, so
 * prefer the unambiguous ones: newlines and semicolons never appear inside a
 * document name, whereas commas do ("Bank statement, last 3 months"). Commas
 * only split a cell that has no other separator to go on.
 */
function requestedDocumentList(documents: string | null): string[] {
  const raw = documents ?? '';
  const separator = /[\n;]/.test(raw) ? /[\n;]/ : /,/;
  // Repeats are kept: Wisetack asks for the same document once per owner, so
  // collapsing "Voided check" twice into one bullet would tell the merchant a
  // single upload clears the application when two are outstanding.
  return raw
    .split(separator)
    .map((entry) => entry.trim())
    .filter((entry) => entry && !NO_DOCUMENTS_SENTINELS.has(entry.toLowerCase()));
}

/**
 * Underwriting stalls silently when Wisetack asks for paperwork: the merchant
 * report carries `requested_documents`, but nothing in Notifi told the merchant
 * about it, so the application just sat in "Under review". It points at the
 * portal where the upload actually happens; `acceptsDocuments` decides the
 * statuses where saying so is still useful.
 */
function RequestedDocumentsNotice({
  documents,
  environment,
}: {
  documents: string | null;
  environment: WisetackSettings['environment'];
}) {
  const items = requestedDocumentList(documents);
  if (items.length === 0) return null;

  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-300/70 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-800/50 dark:bg-amber-950/25 dark:text-amber-100">
      <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1 space-y-2">
        <p className="font-medium">Wisetack needs more documents</p>
        <ul className="list-inside list-disc text-amber-900/80 dark:text-amber-100/80">
          {items.map((item, index) => (
            <li key={`${item}-${index}`}>{item}</li>
          ))}
        </ul>
        <Button asChild type="button" variant="outline" size="sm">
          <a href={wisetackPortalUrl(environment, 'applications')} target="_blank" rel="noreferrer">
            Upload in Wisetack
            <ExternalLink className="size-4" />
          </a>
        </Button>
      </div>
    </div>
  );
}

/**
 * The limits underwriting set for this merchant. Both come straight from the
 * merchant report and are read-only here — Wisetack owns them, and a merchant
 * quoting a job needs to know the ceiling before promising financing.
 */
function MerchantTerms({
  transactionRange,
  pricingPlan,
}: {
  transactionRange: string | null;
  pricingPlan: string | null;
}) {
  const facts = [
    { label: 'Transaction range', value: transactionRange?.trim() },
    { label: 'Pricing plan', value: pricingPlan?.trim() },
  ].filter((fact) => Boolean(fact.value));
  if (facts.length === 0) return null;

  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
      {facts.map((fact) => (
        <div key={fact.label} className="flex gap-1.5">
          <dt className="text-muted-foreground">{fact.label}</dt>
          <dd className="font-medium">{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Mirrors the "Card spec" in Wisetack's low-code guide: mint logo, the
 * "Wisetack Consumer Financing" title, a Faint-Mint "Why offer financing"
 * benefit box, and the SIGN UP / LEARN MORE CTAs in Wisetack's own palette.
 */
function WisetackSignupCard({ enrollmentUrl }: { enrollmentUrl: string | null }) {
  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-start gap-3">
        <BrandLogo id="wisetack" className="mt-0.5" />
        <div>
          <p className="text-sm font-semibold">{WISETACK_CARD_TITLE}</p>
          <p className="text-xs text-muted-foreground">{WISETACK_CARD_SUBTITLE}</p>
        </div>
      </div>

      <WisetackWhyFinancingBox />

      <div className="flex flex-wrap items-center gap-2">
        {enrollmentUrl ? (
          <Button
            asChild
            type="button"
            className="text-white hover:opacity-90"
            style={{ backgroundColor: WISETACK_BRAND.sapphire }}
          >
            <a href={enrollmentUrl} target="_blank" rel="noreferrer">
              SIGN UP
              <ExternalLink className="size-4" />
            </a>
          </Button>
        ) : (
          <div className="space-y-1">
            <Button
              type="button"
              disabled
              className="text-white"
              style={{ backgroundColor: WISETACK_BRAND.sapphire }}
            >
              SIGN UP
              <ExternalLink className="size-4" />
            </Button>
            {/* A signup link is built from the partner alias, so without one
                there is nowhere to send the merchant. Saying so beats a dead
                button nobody can explain. */}
            <p className="text-xs text-muted-foreground">
              Signup is unavailable until Notifi sets this company's Wisetack partner alias.
            </p>
          </div>
        )}
        <Button asChild type="button" variant="outline">
          <a href={WISETACK_LEARN_MORE_URL} target="_blank" rel="noreferrer">
            LEARN MORE
            <ExternalLink className="size-4" />
          </a>
        </Button>
      </div>
    </div>
  );
}

function toDisplaySettings(settings: WisetackSettings | undefined): WisetackSettings {
  const displaySettings = { ...WISETACK_DEFAULT_SETTINGS, ...(settings ?? {}) };
  const environment = String(displaySettings.environment).toLowerCase();
  return {
    ...displaySettings,
    environment: ENVIRONMENT_OPTIONS.some((option) => option.value === environment)
      ? (environment as WisetackEnvironment)
      : WISETACK_DEFAULT_SETTINGS.environment,
  };
}

/**
 * Staging aid, rendered only for a tenant the provisioner switched it on for.
 * It offers the raw `application_status` values Wisetack reports rather than
 * the seven states the app renders, because the mapping between the two — five
 * separate statuses all meaning "still in progress" — is the part worth
 * exercising, and the list comes from the same table operations maps with.
 */
function StatusSimulator({ settings }: { settings: WisetackSettings }) {
  const simulate = useSimulateWisetackStatus();
  // Operations requires `company:edit` for the write. Every other control on
  // the integrations page disables itself for a read-only user rather than
  // letting them fire a request that comes back 403.
  const canEdit = useHasAnyPermission('company:edit');

  return (
    <div className="rounded-md border border-dashed border-amber-300 bg-amber-50/60 p-3">
      <p className="text-xs font-medium text-amber-900">Enrollment status simulator</p>
      <p className="mt-0.5 text-xs text-amber-800">
        Testing aid for this staging company. Picking a status stores the enrollment state it maps
        to, exactly as the merchant report would.
      </p>
      <PermissionHint show={!canEdit}>
        <select
          aria-label="Simulated application status"
          className="mt-2 w-full rounded-md border border-amber-300 bg-white px-2 py-1.5 text-sm disabled:opacity-60"
          disabled={!canEdit || simulate.isPending}
          value={simulatedStatusFor(settings)}
          onChange={(event) => {
            const value = event.target.value;
            simulate.mutate(value === NOT_IN_REPORT ? null : (value as WisetackApplicationStatus));
          }}
        >
          <option value={NOT_IN_REPORT}>Not in the report — Not enrolled</option>
          {WISETACK_APPLICATION_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </PermissionHint>
    </div>
  );
}

const NOT_IN_REPORT = 'NOT_IN_REPORT';

/**
 * Which option to show as selected. Five statuses share `in_progress`, so a
 * stored state cannot name the status it came from; the first status that maps
 * to the current state stands in for the group.
 */
function simulatedStatusFor(settings: WisetackSettings): string {
  const match = WISETACK_APPLICATION_STATUSES.find(
    (status) => WISETACK_APPLICATION_STATUS_MAP[status] === settings.enrollmentState,
  );
  return match ?? NOT_IN_REPORT;
}

/** The Merchant Portal deep links every approved merchant gets. */
function WisetackPortalLinks({ environment }: { environment: WisetackEnvironment }) {
  const links = [
    { path: 'dashboard', label: 'Dashboard' },
    { path: 'applications', label: 'Applications' },
    { path: 'account-settings', label: 'Account settings' },
  ] as const;

  return (
    <div className="flex flex-wrap gap-2">
      {links.map(({ path, label }) => (
        <Button key={path} asChild type="button" variant="outline">
          <a href={wisetackPortalUrl(environment, path)} target="_blank" rel="noreferrer">
            {label}
            <ExternalLink className="size-4" />
          </a>
        </Button>
      ))}
    </div>
  );
}
